import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button, Card } from "@gradebook/ui";
import { captureRenderError } from "@/lib/sentry";
import { CrashReportForm } from "@/components/CrashReportForm";
import { isStaleDeployError } from "@/lib/staleDeploy";

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optional custom fallback. Receives the caught error and a reset callback.
   *
   * A custom fallback replaces the report form below, but not the reporting: the
   * error has already been sent to Sentry by the time this is called.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  /** Sentry's id for this crash, or null when Sentry is not configured. */
  eventId: string | null;
}

/**
 * Catches render-time errors anywhere in the child tree and shows a friendly
 * fallback instead of a blank white screen. Errors thrown in event handlers,
 * async code, or SSR are NOT caught by React error boundaries by design.
 *
 * It also reports the crash, and then asks the person what they were doing.
 * Those two halves belong together: the stack trace says which line threw, and
 * only the person at the keyboard can say that they were saving term grades for
 * a class of thirty when it happened. `sendCrashFeedback` attaches what they
 * write to the event id from `captureRenderError`, so the two arrive as one
 * issue rather than as an unexplained trace and an unattached complaint.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, eventId: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Surface the failure for logging/observability. Kept as console.error so
    // it shows up in the browser console and any error-reporting hook.
    console.error("Uncaught error in React tree:", error, errorInfo);

    // Reported from here rather than from React 19's `onCaughtError` hook on the
    // root (see main.tsx): this is the only place that can hold on to the event
    // id long enough to attach the person's account of it.
    const eventId = captureRenderError(error, errorInfo.componentStack);
    if (eventId) {
      this.setState({ eventId });
    }
  }

  reset = (): void => {
    this.setState({ error: null, eventId: null });
  };

  render(): ReactNode {
    const { error, eventId } = this.state;

    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }

      // A chunk that no longer exists, because the Worker was redeployed while
      // this tab was open. `lib/staleDeploy.ts` normally reloads before anyone
      // sees this; reaching here means its loop guard declined, which is the
      // case where the reload already happened once and did not help.
      //
      // Worth a card of its own because the generic one is actively misleading
      // here: nothing has gone wrong with the app, nothing is worth reporting,
      // and "Try again" re-runs the same import against the same missing file,
      // so it can never succeed.
      if (isStaleDeployError(error)) {
        return (
          <div
            role="alert"
            className="flex min-h-screen items-center justify-center p-4"
          >
            <Card className="w-full max-w-md" padding="lg">
              <div className="text-center">
                <h1 className="mb-2 text-xl font-semibold text-foreground">
                  A new version is available
                </h1>
                <p className="mb-6 text-sm text-muted-foreground">
                  This page is out of date. Reload to continue — you will come
                  back to the page you were opening.
                </p>
                <div className="flex justify-center">
                  <Button onClick={() => window.location.reload()}>
                    Reload page
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );
      }

      return (
        <div
          role="alert"
          className="flex min-h-screen items-center justify-center p-4"
        >
          <Card className="w-full max-w-md" padding="lg">
            <div className="text-center">
              <h1 className="mb-2 text-xl font-semibold text-foreground">
                Something went wrong
              </h1>
              <p className="mb-6 text-sm text-muted-foreground">
                An unexpected error occurred. Try reloading the page — if the
                problem persists, contact your administrator.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={this.reset}>
                  Try again
                </Button>
                <Button onClick={() => window.location.reload()}>
                  Reload page
                </Button>
              </div>
            </div>

            {/* Only when the crash actually reached Sentry. Without an event to
                attach it to, a form here would collect an account of a problem
                nobody can look up — worse than not asking. */}
            {eventId && <CrashReportForm eventId={eventId} />}
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
